/**
 * Toasts (Radix Toast) com uma store mínima: toast.success("Publicado").
 * Pode ser chamado fora de componentes (ex.: callbacks de mutations).
 */
import * as ToastPrimitive from "@radix-ui/react-toast";
import { AlertCircle, CheckCircle2, Info, X } from "lucide-react";
import { useSyncExternalStore } from "react";
import { t } from "@/i18n";
import { errorMessage } from "@/lib/errors";
import { cn } from "@/lib/utils";

type Tone = "success" | "error" | "info";
type ToastItem = { id: number; title: string; description?: string | undefined; tone: Tone; open: boolean };

let items: ToastItem[] = [];
let seq = 0;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

function push(tone: Tone, title: string, description?: string) {
  items = [...items.slice(-3), { id: ++seq, title, description, tone, open: true }];
  emit();
}

function dismiss(id: number) {
  items = items.map((i) => (i.id === id ? { ...i, open: false } : i));
  emit();
  setTimeout(() => {
    items = items.filter((i) => i.id !== id);
    emit();
  }, 300);
}

export const toast = {
  success: (title: string, description?: string) => push("success", title, description),
  info: (title: string, description?: string) => push("info", title, description),
  /** Aceita um erro (ApiError etc.) ou um texto. */
  error: (err: unknown, title?: string) =>
    typeof err === "string" ? push("error", err) : push("error", title ?? t("common.errorTitle"), errorMessage(err)),
  /** "Publicado" — usado em toda alteração que vai direto para o edge. */
  published: () => push("success", t("common.published"), t("common.publishedDesc")),
};

/** Apenas para testes. */
export const __toasts = () => items;

const ICONS = { success: CheckCircle2, error: AlertCircle, info: Info };

export function Toaster() {
  const list = useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => items,
    () => items,
  );
  return (
    <ToastPrimitive.Provider swipeDirection="right" duration={4500} label={t("common.close")}>
      {list.map((item) => {
        const Icon = ICONS[item.tone];
        return (
          <ToastPrimitive.Root
            key={item.id}
            open={item.open}
            onOpenChange={(open) => !open && dismiss(item.id)}
            type={item.tone === "error" ? "foreground" : "background"}
            duration={item.tone === "error" ? 7000 : 4500}
            className={cn(
              "animate-in pointer-events-auto relative flex w-full items-start gap-3 rounded-lg border bg-popover p-3 pr-8 text-popover-foreground shadow-lg",
              item.tone === "error" && "border-destructive/40",
            )}
          >
            <Icon
              className={cn(
                "mt-0.5 size-4 shrink-0",
                item.tone === "success" && "text-success",
                item.tone === "error" && "text-destructive",
                item.tone === "info" && "text-info",
              )}
              aria-hidden
            />
            <div className="grid gap-0.5">
              <ToastPrimitive.Title className="text-sm font-medium">{item.title}</ToastPrimitive.Title>
              {item.description && (
                <ToastPrimitive.Description className="text-xs text-muted-foreground">{item.description}</ToastPrimitive.Description>
              )}
            </div>
            <ToastPrimitive.Close className="absolute right-2 top-2 rounded p-0.5 text-muted-foreground hover:text-foreground" aria-label={t("common.close")}>
              <X className="size-3.5" />
            </ToastPrimitive.Close>
          </ToastPrimitive.Root>
        );
      })}
      <ToastPrimitive.Viewport className="fixed bottom-0 right-0 z-[100] flex w-full max-w-sm flex-col gap-2 p-4 outline-none" />
    </ToastPrimitive.Provider>
  );
}
